/**
 * LotSmith — Recent trades table with symbol filter, sort, pagination,
 * and an expandable per-row journal note (M-Trade-Journal v0).
 *
 * Notes are user-edited only; the EA never writes them. Submitting saves via
 * the data context (DevProvider keeps in-memory; LiveProvider PATCHes the
 * worker with optimistic update + rollback on failure).
 */
import { useEffect, useMemo, useState } from "react";
import type { DemoTrade } from "../data/demo.js";
import { useDev } from "../providers/dataContext.js";

type SortKey = "ts" | "symbol" | "volume" | "pnl";
type SortDir = "asc" | "desc";

interface Props { trades: DemoTrade[]; pageSize?: number; }

export function RecentTrades({ trades, pageSize = 10 }: Props) {
  const [filter, setFilter] = useState<string>("");
  const [sortKey, setSortKey] = useState<SortKey>("ts");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [shown, setShown] = useState(pageSize);
  const [expanded, setExpanded] = useState<string | null>(null);

  const symbols = useMemo(() => {
    return Array.from(new Set(trades.map((t) => t.symbol))).sort();
  }, [trades]);

  const filtered = useMemo(() => {
    const list = filter ? trades.filter((t) => t.symbol === filter) : trades.slice();
    list.sort((a, b) => {
      const av = pickSort(a, sortKey);
      const bv = pickSort(b, sortKey);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [trades, filter, sortKey, sortDir]);

  function clickHeader(k: SortKey) {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  }

  if (trades.length === 0) {
    return <div className="trades-empty">No trades yet. The EA will populate this once live.</div>;
  }

  const visible = filtered.slice(0, shown);

  return (
    <div className="trades">
      <div className="trades__bar">
        <div className="trades__filter">
          <button
            className={`chip ${filter === "" ? "chip--active" : ""}`}
            onClick={() => setFilter("")}
          >
            All
          </button>
          {symbols.map((s) => (
            <button
              key={s}
              className={`chip ${filter === s ? "chip--active" : ""}`}
              onClick={() => setFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="trades__count">
          {filtered.length} {filtered.length === 1 ? "trade" : "trades"}
        </div>
      </div>

      <table className="trades__table num">
        <thead>
          <tr>
            <th aria-label="expand" />
            <th><Sortable label="Time"   k="ts"     sortKey={sortKey} sortDir={sortDir} onClick={clickHeader} /></th>
            <th><Sortable label="Symbol" k="symbol" sortKey={sortKey} sortDir={sortDir} onClick={clickHeader} /></th>
            <th className="hide-sm">Dir</th>
            <th className="r"><Sortable label="Vol"  k="volume" sortKey={sortKey} sortDir={sortDir} onClick={clickHeader} /></th>
            <th className="r"><Sortable label="P&L"  k="pnl"    sortKey={sortKey} sortDir={sortDir} onClick={clickHeader} /></th>
          </tr>
        </thead>
        <tbody>
          {visible.map((t) => {
            const isOpen = expanded === t.id;
            return (
              <TradeRowGroup
                key={t.id}
                trade={t}
                isOpen={isOpen}
                onToggle={() => setExpanded(isOpen ? null : t.id)}
              />
            );
          })}
        </tbody>
      </table>

      {filtered.length > shown && (
        <button
          type="button"
          className="trades__more"
          onClick={() => setShown((n) => n + pageSize)}
        >
          Load more ({filtered.length - shown} left)
        </button>
      )}
    </div>
  );
}

interface TradeRowGroupProps {
  trade: DemoTrade;
  isOpen: boolean;
  onToggle: () => void;
}

function TradeRowGroup({ trade, isOpen, onToggle }: TradeRowGroupProps) {
  const hasNote = !!(trade.note && trade.note.trim().length > 0);
  return (
    <>
      <tr className="trades__row">
        <td className="trades__expand-cell">
          <button
            type="button"
            className={`trades__expand ${isOpen ? "is-open" : ""}`}
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? "Hide note" : hasNote ? "View note" : "Add note"}
            title={hasNote ? (isOpen ? "Hide note" : "View note") : "Add note"}
          >
            <span aria-hidden="true">{hasNote ? "●" : "+"}</span>
          </button>
        </td>
        <td className="t-time">{formatTime(trade.ts)}</td>
        <td>{trade.symbol}</td>
        <td className={`hide-sm dir dir--${trade.direction.toLowerCase()}`}>{trade.direction}</td>
        <td className="r">{trade.volume.toFixed(2)}</td>
        <td className={`r ${trade.pnl >= 0 ? "tone-profit" : "tone-loss"}`}>
          {trade.pnl >= 0 ? "+" : "-"}${Math.abs(trade.pnl).toFixed(2)}
        </td>
      </tr>
      {isOpen && (
        <tr className="trades__journal-row">
          <td colSpan={6}>
            <NoteEditor trade={trade} onClose={onToggle} />
          </td>
        </tr>
      )}
    </>
  );
}

interface NoteEditorProps {
  trade: DemoTrade;
  onClose: () => void;
}

const MAX_NOTE_LEN = 1000;

const MOODS: Array<{ id: import("../data/demo.js").Mood; emoji: string; label: string }> = [
  { id: "calm",       emoji: "😌", label: "Calm" },
  { id: "confident",  emoji: "🎯", label: "Confident" },
  { id: "anxious",    emoji: "😰", label: "Anxious" },
  { id: "uncertain",  emoji: "🤔", label: "Uncertain" },
  { id: "revenge",    emoji: "😡", label: "Revenge" },
];

const SETUP_PRESETS = [
  "Trend continuation", "Breakout", "Reversal", "Range",
  "London session", "NY session", "Asia session",
  "News driven", "Confluence",
];
const MISTAKE_PRESETS = [
  "Entered too early", "Moved SL", "Closed too early",
  "Oversized", "No clear plan", "Ignored news", "Wrong session",
];

function NoteEditor({ trade, onClose }: NoteEditorProps) {
  const { updateTradeNote, updateTradeJournal } = useDev();
  const [draft, setDraft] = useState<string>(trade.note ?? "");
  const [mood, setMood] = useState<import("../data/demo.js").Mood | null>(trade.mood ?? null);
  const [setupTags, setSetupTags] = useState<string[]>(trade.setupTags ?? []);
  const [mistakeTags, setMistakeTags] = useState<string[]>(trade.mistakeTags ?? []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // If the trade prop changes (e.g. external reload), refresh state.
  useEffect(() => {
    setDraft(trade.note ?? "");
    setMood(trade.mood ?? null);
    setSetupTags(trade.setupTags ?? []);
    setMistakeTags(trade.mistakeTags ?? []);
    setErr(null);
  }, [trade.id, trade.note, trade.mood, trade.setupTags, trade.mistakeTags]);

  const original = trade.note ?? "";
  const noteDirty = draft !== original;
  const moodDirty = mood !== (trade.mood ?? null);
  const setupDirty = !sameTags(setupTags, trade.setupTags ?? []);
  const mistakeDirty = !sameTags(mistakeTags, trade.mistakeTags ?? []);
  const dirty = noteDirty || moodDirty || setupDirty || mistakeDirty;
  const tooLong = draft.length > MAX_NOTE_LEN;

  function toggleTag(set: string[], setSet: (next: string[]) => void, tag: string) {
    if (set.includes(tag)) setSet(set.filter((t) => t !== tag));
    else if (set.length < 8) setSet([...set, tag]);
  }

  async function save() {
    if (!dirty || tooLong || saving) return;
    setSaving(true); setErr(null);
    try {
      // Send the note + the structured journal in two calls (note has its
      // own dirty flag and goes through updateTradeNote so the optimistic
      // path matches the existing one).
      if (noteDirty) {
        const next = draft.trim().length === 0 ? null : draft;
        await updateTradeNote(trade.id, next);
      }
      if (moodDirty || setupDirty || mistakeDirty) {
        await updateTradeJournal(trade.id, {
          ...(moodDirty ? { mood } : {}),
          ...(setupDirty ? { setupTags: setupTags.length === 0 ? null : setupTags } : {}),
          ...(mistakeDirty ? { mistakeTags: mistakeTags.length === 0 ? null : mistakeTags } : {}),
        });
      }
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save the journal entry.");
    } finally {
      setSaving(false);
    }
  }

  async function clearNote() {
    if (saving) return;
    setSaving(true); setErr(null);
    try {
      await updateTradeNote(trade.id, null);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't clear the note.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="journal"
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); void save(); }
        if (e.key === "Escape") onClose();
      }}
    >
      {/* Mood — single-select emoji row */}
      <div className="journal__group">
        <div className="journal__label">How did this feel?</div>
        <div className="journal__moods">
          {MOODS.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`mood-chip ${mood === m.id ? "is-active" : ""}`}
              onClick={() => setMood(mood === m.id ? null : m.id)}
              disabled={saving}
              aria-pressed={mood === m.id}
            >
              <span className="mood-chip__emoji" aria-hidden>{m.emoji}</span>
              <span className="mood-chip__label">{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Setup tags */}
      <div className="journal__group">
        <div className="journal__label">Setup</div>
        <div className="journal__tags">
          {SETUP_PRESETS.map((tag) => (
            <button
              key={tag}
              type="button"
              className={`tag-chip ${setupTags.includes(tag) ? "is-active" : ""}`}
              onClick={() => toggleTag(setupTags, setSetupTags, tag)}
              disabled={saving}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Mistake tags */}
      <div className="journal__group">
        <div className="journal__label">
          Mistakes <span className="journal__sub">be honest with yourself</span>
        </div>
        <div className="journal__tags">
          {MISTAKE_PRESETS.map((tag) => (
            <button
              key={tag}
              type="button"
              className={`tag-chip tag-chip--mistake ${mistakeTags.includes(tag) ? "is-active" : ""}`}
              onClick={() => toggleTag(mistakeTags, setMistakeTags, tag)}
              disabled={saving}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Free-form note */}
      <div className="journal__group">
        <label className="journal__label" htmlFor={`note-${trade.id}`}>
          Notes
        </label>
        <textarea
          id={`note-${trade.id}`}
          className="journal__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Strong London open momentum after NFP repricing…"
          rows={3}
          maxLength={MAX_NOTE_LEN + 200}
          disabled={saving}
        />
      </div>

      <div className="journal__bar">
        <div className={`journal__count ${tooLong ? "is-error" : ""}`}>
          {draft.length} / {MAX_NOTE_LEN}
        </div>
        <div className="journal__actions">
          {original.length > 0 && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => void clearNote()}
              disabled={saving}
            >
              Clear note
            </button>
          )}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => void save()}
            disabled={!dirty || tooLong || saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      {err && <div className="journal__error" role="alert">{err}</div>}
    </div>
  );
}

function sameTags(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function pickSort(t: DemoTrade, k: SortKey): number | string {
  if (k === "ts") return new Date(t.ts).getTime();
  if (k === "symbol") return t.symbol;
  if (k === "volume") return t.volume;
  return t.pnl;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

interface SortableProps {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
}
function Sortable({ label, k, sortKey, sortDir, onClick }: SortableProps) {
  const active = k === sortKey;
  return (
    <button
      type="button"
      className={`th-sort${active ? " is-active" : ""}`}
      onClick={() => onClick(k)}
    >
      {label}
      {active && <span className="th-sort__caret">{sortDir === "asc" ? "▲" : "▼"}</span>}
    </button>
  );
}
